import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "redux/store";
import { Link, useNavigate } from "react-router-dom";
import Button from "style-guide/component/Button";
import Input from "style-guide/component/Input";
import Typography from "style-guide/component/Typography";
import { login } from "../../redux/slice/rfpModuleSlice/application";
import { EyeInvisibleOutlined, EyeOutlined } from "@ant-design/icons";
import { getCookie } from "utils/utils";
import Google from "style-guide/svg-components/GoogleSvg";
import MicrosoftSvg from "style-guide/svg-components/MicrosoftSvg";
import Gradient from "style-guide/component/Gradient";

const Login = () => {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [passwordValue, setPasswordValue] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [emailError, setEmailError] = useState("");
  const [passwordError, setPasswordError] = useState("");
  const { isLoading } = useSelector(
    (state: RootState) => state.applicationData
  );
  const cookieValue = getCookie("token");

  const validateEmail = (email: string) =>
    /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

  const handleLogin = async () => {
    let valid = true;
    setEmailError("");
    setPasswordError("");

    if (!email) {
      setEmailError("Email is required.");
      valid = false;
    } else if (!validateEmail(email)) {
      setEmailError("Please enter a valid email address.");
      valid = false;
    }

    if (!passwordValue) {
      setPasswordError("Password is required.");
      valid = false;
    } else if (passwordValue.length < 6) {
      setPasswordError("Password must be at least 6 characters.");
      valid = false;
    }

    if (!valid) return;

    document.cookie = "rfp_id=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;";
    const result = await dispatch(
      login({ login: email, password: passwordValue })
    );

    // Check if 2FA is required
    if (result?.requires2FA) {
      navigate("/two-factor-auth");
    }
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === "Enter") handleLogin();
  };

  const togglePasswordVisibility = () => {
    setShowPassword((prev) => !prev);
  };

  useEffect(() => {
    if (cookieValue !== null) navigate("/home");
  }, [cookieValue, navigate]);

  return (
    <Gradient>
      <div className="bg-white rounded-2xl shadow-2xl px-8 py-4 space-y-4 sm:w-[500px]">
        <Typography
          text="Welcome, Enter your credentials"
          variant="Heading 3"
          strong
          className="text-center text-blue-900"
        />

        <div>
          <Typography
            text="Enter your work email"
            variant="Base"
            className="mb-2 text-gray-700"
          />
          <Input
            placeholder="Enter first name"
            value={email}
            theme="light"
            onChange={(e) => setEmail(e.target.value)}
            onKeyDown={handleKeyDown}
            state="Default"
            size="Medium"
            className="w-full rounded-lg border-gray-300"
            disabled={isLoading}
          />
          {emailError && (
            <Typography variant="Base" className="text-red-500 mt-1">
              {emailError}
            </Typography>
          )}
        </div>

        <div className="relative">
          <Typography
            text="Password"
            variant="Base"
            className="mb-2 text-gray-700"
          />
          <Input
            type={showPassword ? "text" : "password"}
            placeholder="Password"
            value={passwordValue}
            onChange={(e) => setPasswordValue(e.target.value)}
            onKeyDown={handleKeyDown}
            state="Default"
            size="Medium"
            theme="light"
            className="w-full rounded-lg border-gray-300"
            disabled={isLoading}
          />
          <button
            type="button"
            onClick={togglePasswordVisibility}
            className="absolute right-3 top-10 text-gray-400 hover:text-gray-600"
          >
            {showPassword ? <EyeOutlined /> : <EyeInvisibleOutlined />}
          </button>
          {passwordError && (
            <Typography variant="Base" className="text-red-500 mt-1">
              {passwordError}
            </Typography>
          )}
        </div>

        <div className="flex items-start justify-end">
          <Typography
            text="Forgot your password?"
            variant="Base"
            className="text-blue-600 hover:!underline cursor-pointer"
            onClick={() => navigate("/forgot-password")}
          />
        </div>

        <Button
          type="Primary"
          loading={isLoading}
          onClick={handleLogin}
          buttonText="Log In"
          theme="light"
          className="w-full py-3 hover:bg-blue-700 rounded-lg"
        />

        <div className="flex items-center space-x-4 my-1">
          <div className="flex-1 h-px bg-gray-300" />
          <Typography text="or" variant="Base" className="text-gray-500 px-3" />
          <div className="flex-1 h-px bg-gray-300" />
        </div>

        <div className="flex items-center justify-center gap-2">
          <Button
            type="Default"
            icon
            iconComponent={<Google />}
            buttonText="Sign up with Google"
            className="w-full py-3 border-gray-300 opacity-50 cursor-default rounded-lg"
          />
          <Button
            type="Default"
            icon
            iconComponent={<MicrosoftSvg />}
            buttonText="Sign up with Microsoft"
            className="w-full py-3 border-gray-300 opacity-50 cursor-default rounded-lg"
          />
        </div>

        <Typography variant="Base" className="text-center mt-4 text-gray-600">
          Don't have an account yet?{" "}
          <Link className="text-[#0A4BAA] hover:underline" to={"/signup"}>
            Signup
          </Link>
        </Typography>
      </div>
    </Gradient>
  );
};

export default Login;
